import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {
  Stack,
  Box,
  Text,
  Label,
  Button,
  TextInput,
  Card,
  Flex,
  Dialog,
  Badge,
  Inline,
  Select,
} from '@sanity/ui'
import {Code} from '@sanity/ui/code'
import {set, unset, useFormValue, StringInputProps} from 'sanity'
import {debounce} from '../utils/debounce'

// Generate unique key for array items
const generateKey = () => `key-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

interface Product {
  _key?: string
  sku: string
  name: string
  id: string | number
  productType: string
  customLabel?: string
}

interface SearchFilter {
  code: string
  name: string
  options: Record<string, string>
}

interface AccessorySkusAltProps extends StringInputProps {
  // Additional props if needed
}

const AccessorySkusAlt = React.forwardRef<HTMLDivElement, AccessorySkusAltProps>((props, ref) => {
  const {value, onChange} = props
  const document = useFormValue([]) as any

  // Component state
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchResults, setSearchResults] = useState<Product[]>([])
  const [isLoaded, setIsLoaded] = useState(false)
  const [data, setData] = useState<Product[]>([])
  const [searchFilters, setSearchFilters] = useState<SearchFilter[]>([])
  const [selectedFilters, setSelectedFilters] = useState<Record<string, any>>({})

  // Get frontend URL from environment or fallback (original pattern)
  const baseURL = process.env.SANITY_STUDIO_FE_URL || 'http://localhost:3000'

  // Get attributes and set up search filters
  const getAttributes = useCallback(async () => {
    try {
      const response = await fetch(`${baseURL}/api/sync/content/data-attributes`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const json = await response.json()
      const data = json?.data || json
      const {attributeSets = {}} = data || {}

      setSearchFilters([
        {
          code: 'kind',
          name: 'Kind',
          options: Object.keys(attributeSets)
            .filter((key) => !['Default', 'apex_wheels'].includes(attributeSets[key]))
            .reduce(
              (acc, key) => ({
                ...acc,
                [key]: attributeSets[key],
              }),
              {},
            ),
        },
      ])
    } catch (error) {
      console.error('Error fetching attributes:', error)
    }
  }, [baseURL])

  useEffect(() => {
    getAttributes()
    setIsLoaded(true)
  }, [getAttributes])

  // Initialize data from existing value
  // Note: Missing keys are fixed by ProductCombinationInput wrapper
  useEffect(() => {
    if (value && Array.isArray(value) && value.length > 0) {
      const productsWithKeys = (value as Product[]).map((product) => ({
        ...product,
        _key: product._key || generateKey(),
      }))
      setData(productsWithKeys)
    } else if (!value || (Array.isArray(value) && value.length === 0)) {
      setData([])
    }
  }, [value])

  // Use document._createdAt to load local data (legacy support)
  useEffect(() => {
    if (!document?._createdAt) return
    // Delay setting local data slightly
    setTimeout(() => {
      if (value && Array.isArray(value)) {
        const productsWithKeys = (value as Product[]).map(product => ({
          ...product,
          _key: product._key || generateKey()
        }))
        setData(productsWithKeys)
      }
    }, 100)
  }, [document?._createdAt, value])

  // Filter functions (same as AccessoriesSkus)
  const filterAlreadyAdded = useCallback(
    (product: Product) => {
      return !data.map(({sku}) => sku).includes(product.sku)
    },
    [data],
  )

  // Search products
  const searchProducts = useCallback(async () => {
    if (searching) return

    setSearching(true)

    try {
      const kind = selectedFilters?.kind?.value
      const params: any = {}

      if (query.length) params.sku = query
      if (kind) params.attribute_set_id = kind

      // Build query parameters
      const searchParams = new URLSearchParams({
        ...params,
        apex_configurator: '1',
        type_id: 'any',
      })

      const response = await fetch(`${baseURL}/api/callbacks/search-products?${searchParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const items = (await response.json()) || []

      if (!items?.length) {
        setSearchResults([])
        return
      }

      const results = items.reduce((map: Product[], product: any) => {
        // Keep only relevant information
        const productItem = {
          sku: product.sku,
          name: product.name,
          id: product.id,
          productType: product.type_id,
        }

        map.push(productItem)
        return map
      }, [])

      // Apply client-side filtering to remove already added products
      const filteredResults = results.filter(filterAlreadyAdded)
      setSearchResults(filteredResults)
    } catch (error) {
      console.error('Search products error:', error)
    } finally {
      setSearching(false)
    }
  }, [searching, selectedFilters, query, baseURL, data, filterAlreadyAdded])

  // Handle value changes
  const updateData = useCallback(
    (newValue: Product[] = []) => {
      setData(newValue)

      if (!isLoaded) {
        return
      }

      onChange(newValue ? set(newValue) : unset())
    },
    [isLoaded, onChange],
  )

  // Product management
  const addProduct = useCallback(
    (product: Product) => {
      // Filter out selected product from searchResults
      setSearchResults((prev) => prev.filter((item) => item.sku !== product.sku))

      // Add selected product to data with unique _key
      const productWithKey = {
        ...product,
        _key: product._key || generateKey()
      }
      updateData([...data, productWithKey])
    },
    [data, updateData],
  )

  const removeProduct = useCallback(
    (product: Product) => {
      // Filter out selected product
      const products = data.filter((item) => item.sku !== product.sku)
      updateData(products)
    },
    [data, updateData],
  )

  // Debounced product update function
  const updateProduct = useMemo(
    () =>
      debounce((product: Product) => {
        // Replace old product with new one, keep others the same
        const products = data.map((item) => (item.sku === product.sku ? product : item))
        updateData(products)
      }, 300),
    [data, updateData],
  )

  // Filter management
  const updateSearchFilter = useCallback((code: string, value: any) => {
    setSelectedFilters((prev) => ({...prev, [code]: value}))
  }, [])

  const clearFilters = useCallback(() => {
    setSelectedFilters({})
  }, [])

  // Dialog management
  const onDialogClose = useCallback(() => {
    setSearchResults([])
  }, [])

  // Helper function to get select options
  const getSelectOptions = useCallback((obj: Record<string, string>) => {
    const keys = Object.keys(obj || {})
    return keys.map((key) => ({value: key, label: obj[key]}))
  }, [])

  return (
    <Stack gap={4} ref={ref}>
      {/* Search Filters */}
      <Box>
        <Label size={1} weight="medium">
          Search Filters
        </Label>
        <Flex gap={3} wrap="wrap">
          {searchFilters.map((filter, filterIndex) => (
            <Box key={`filter-${filterIndex}`} style={{minWidth: '200px'}}>
              <Label size={0}>{filter.name}</Label>
              <Flex gap={2}>
                <Select
                  value={selectedFilters[filter.code]?.value || ''}
                  onChange={(event) => {
                    const value = event.currentTarget.value
                    const selectedOption = getSelectOptions(filter.options).find(
                      (opt) => opt.value === value,
                    )
                    updateSearchFilter(filter.code, selectedOption || null)
                  }}
                  style={{flex: 1}}
                >
                  <option value="">Select...</option>
                  {getSelectOptions(filter.options).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                {selectedFilters[filter.code] && (
                  <Button
                    mode="ghost"
                    tone="default"
                    onClick={() => updateSearchFilter(filter.code, null)}
                    text="Clear"
                   
                  />
                )}
              </Flex>
            </Box>
          ))}
          <Box style={{alignSelf: 'flex-end'}}>
            <Button mode="ghost" text="Clear Filters" onClick={clearFilters} tone="caution" />
          </Box>
        </Flex>
      </Box>

      {/* Search Input */}
      <Box>
        <Label size={1} weight="medium">
          Product Search
        </Label>
        <Flex gap={2}>
          <Box flex={1}>
            <TextInput
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder="Search by SKU..."
            />
          </Box>
          <Button
            text={searching ? 'Searching...' : 'Search'}
            onClick={searchProducts}
            disabled={searching}
            tone="primary"
          />
        </Flex>
      </Box>

      {/* Search Results Dialog */}
      {searchResults.length > 0 && (
        <Dialog
          header={`${searchResults.length} products found`}
          id="search-results"
          onClose={onDialogClose}
          width={1}
          zOffset={1000}
        >
          <Box padding={4}>
            <Stack gap={3}>
              {searchResults.map((product) => (
                <Card padding={3} shadow={1} tone="positive" key={product.sku}>
                  <Stack gap={2}>
                    <Flex align="baseline" justify="space-between" gap={2}>
                      <Text size={2} weight="medium">
                        {product.name}
                      </Text>
                      <Button text="Add" onClick={() => addProduct(product)} tone="positive" />
                    </Flex>

                    {product.sku && <Code size={1}>{product.sku}</Code>}

                    {product.productType && (
                      <Inline>
                        <Badge fontSize={1} tone="primary">
                          {product.productType}
                        </Badge>
                      </Inline>
                    )}
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Box>
        </Dialog>
      )}

      {/* Selected Products */}
      {data?.length > 0 && (
        <Box>
          <Text size={2} weight="medium">
            Selected:
          </Text>
          <Stack gap={3}>
            {data.map((product) => (
              <Card padding={3} shadow={1} tone="primary" key={product._key || product.sku}>
                <Stack gap={2}>
                  <Flex align="baseline" justify="space-between" gap={2}>
                    <Text size={2} weight="medium">
                      {product.name}
                    </Text>
                    <Button
                      text="Remove"
                      mode="ghost"
                      onClick={() => removeProduct(product)}
                      tone="critical"
                    />
                  </Flex>

                  {product.sku && <Code size={1}>{product.sku}</Code>}

                  {product.productType && (
                    <Inline>
                      <Badge fontSize={1} tone="primary">
                        {product.productType}
                      </Badge>
                    </Inline>
                  )}

                  {/* Custom Label for Simple Products */}
                  {product.productType === 'simple' && (
                    <Stack gap={2} marginTop={3}>
                      <Flex align="center" justify="space-between" gap={4}>
                        <Label size={1}>Custom Label</Label>
                        <Box flex={1}>
                          <TextInput
                            defaultValue={product.customLabel || ''}
                            onChange={(event) => {
                              updateProduct({
                                ...product,
                                customLabel: event.currentTarget.value,
                              })
                            }}
                            placeholder="Enter custom label"
                          />
                        </Box>
                      </Flex>
                      <Text size={1} muted>
                        This custom label will be used in the dropdown selection
                      </Text>
                    </Stack>
                  )}
                </Stack>
              </Card>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  )
})

AccessorySkusAlt.displayName = 'AccessorySkusAlt'

export default AccessorySkusAlt
